import { useTheme } from "@emotion/react";
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Grid,
} from "@mui/material";
import { tokens } from "../../theme";
import FormService from "../../services/doctorServices/FormService";
import { useParams } from "react-router-dom";
import PublicFormPageTopbar from "../../components/PublicFormPageTopbar";
import SnackbarComponent from "../../components/SnackbarComponent";
import TableChartIcon from "@mui/icons-material/TableChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ImageIcon from "@mui/icons-material/Image";
import CartilageMap from "../../components/CartilageMap";
import ImageDrawingAnswer from "../../components/ImageDrawingAnswer";
import RatingScaleSlider from "../../components/RatingScaleSlider";
import { useTranslation } from "react-i18next";
import Seo from "../../seo/Seo";

export default function PublicFormPage() {
  const { token } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const formService = useMemo(() => new FormService(), []);
  const { t, i18n } = useTranslation('publicForm');
  const { t: tSeo } = useTranslation('seo');

  // Get locale for date formatting based on current language
  const getDateLocale = () => {
    const currentLang = i18n.language || 'en';
    if (currentLang.startsWith('tr')) return 'tr-TR';
    if (currentLang.startsWith('fr')) return 'fr-FR';
    return 'en-US';
  };

  const [formData, setFormData] = useState(null);
  const [formInfo, setFormInfo] = useState(null);
  const [formStatus, setFormStatus] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [patient, setPatient] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  /** Daha önce gönderilmiş form (aynı davet linkiyle tekrar gelindiğinde) */
  const [alreadySubmittedInfo, setAlreadySubmittedInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState({ open: false, message: "" });
  const [submitError, setSubmitError] = useState({ open: false, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set body and html background to white when component mounts
  useEffect(() => {
    const originalBodyBackground = document.body.style.background;
    const originalHtmlBackground = document.documentElement.style.background;
    document.body.style.background = "white";
    document.documentElement.style.background = "white";
    
    return () => {
      document.body.style.background = originalBodyBackground;
      document.documentElement.style.background = originalHtmlBackground;
    };
  }, []);

  // --- Form bilgisini çek ---
  useEffect(() => {
    const fetchFormInformation = async () => {
      setLoading(true);
      setError(null);
      setAlreadySubmittedInfo(null);
      try {
        const response = await formService.getPublicFormInfo(token);
        console.log("Form bilgileri geldi:", response);
        console.log("Response.form:", response.form);
        console.log("Response keys:", Object.keys(response));

        if (response.formAlreadySubmitted) {
          setAlreadySubmittedInfo({
            answeredAt: response.answeredAt,
            formName: response.form?.name ?? null,
          });
          setLoading(false);
          return;
        }

        // Backend'den gelen detaylı bilgileri ayır
        // Backend response.form döndürüyor, direkt response da form olabilir
        const formData = response.form || response;

        if (!formData) {
          console.error("Form data bulunamadı:", response);
          setError(t('formDataCouldNotBeLoaded'));
          setLoading(false);
          return;
        }

        setFormData(response);
        setFormInfo(formData);
        setFormStatus(response.formStatus || null);
        setInvitation(response.invitation || null);
        setPatient(response.patient || null);

        console.log("Form info set edildi:", formData);
        setLoading(false);
      } catch (error) {
        console.error("Form bilgileri getirilirken bir sorun oluştu:", error);
        console.error("Error details:", error.response?.data || error.message);
        setError(error.response?.data?.message || error.message || t('failedToLoadForm'));
        setLoading(false);
      }
    };
    if (token) {
      fetchFormInformation();
    } else {
      setError(t('tokenRequired'));
      setLoading(false);
    }
  }, [token, formService, t]);

  useEffect(() => {
    if (!formInfo?.questions?.length) return;
    setAnswers((prev) => {
      const next = { ...prev };
      let changed = false;
      formInfo.questions.forEach((q, i) => {
        if (q.type === "rating_scale" && next[i] === undefined) {
          next[i] = 5;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [formInfo]);

  // --- Cevap değişikliğini kaydet ---
  const handleAnswerChange = (index, value, optionLevel = null) => {
    setAnswers((prev) => ({
      ...prev,
      [index]: value,
      ...(optionLevel !== null && { [`${index}_option_level`]: optionLevel }),
    }));
  };

  // --- Tablo soruları için özel handler (her satırda en fazla 1 seçenek) ---
  const handleTableAnswerChange = (questionIndex, rowIndex, headerIdx, header) => {
    const cellKey = `${rowIndex}_${headerIdx}`;
    setAnswers((prev) => {
      const currentQuestionAnswers = prev[questionIndex] || {};
      const isCurrentlySelected = currentQuestionAnswers[cellKey] === true;
      
      // Yeni state objesi oluştur
      const newQuestionAnswers = { ...currentQuestionAnswers };
      
      if (isCurrentlySelected) {
        // Seçiliyse kaldır
        delete newQuestionAnswers[cellKey];
      } else {
        // Seçili değilse, önce aynı satırdaki diğer seçimleri kaldır
        Object.keys(newQuestionAnswers).forEach(key => {
          if (key.startsWith(`${rowIndex}_`) && key !== cellKey) {
            delete newQuestionAnswers[key];
          }
        });
        // Sonra yeni seçimi ekle
        newQuestionAnswers[cellKey] = true;
      }
      
      return {
        ...prev,
        [questionIndex]: newQuestionAnswers,
      };
    });
  };

  // --- Form validasyonu ---
  const validateForm = () => {
    if (!formInfo?.questions || formInfo.questions.length === 0) {
      return { isValid: true, message: "" };
    }

    for (let i = 0; i < formInfo.questions.length; i++) {
      const question = formInfo.questions[i];
      const answer = answers[i];

      if (question.type === "text") {
        if (!answer || answer.trim() === "") {
          return {
            isValid: false,
            message: t('questionRequired', { number: i + 1 })
          };
        }
      }

      if (question.type === "multiple_choice") {
        if (!answer || answer === "") {
          return {
            isValid: false,
            message: t('questionRequiredSelect', { number: i + 1 })
          };
        }
      }

      if (question.type === "table") {
        if (!answer || typeof answer !== "object") {
          return {
            isValid: false,
            message: t('tableQuestionRequired', { number: i + 1 })
          };
        }

        // Her satırda en az bir seçim olmalı
        const selectedCells = Object.keys(answer).filter(key => answer[key] === true);
        const selectedRows = new Set(selectedCells.map(cellKey => cellKey.split('_')[0]));
        const totalRows = question.tableRows;

        if (selectedRows.size < totalRows) {
          return {
            isValid: false,
            message: t('tableQuestionIncomplete', { number: i + 1, total: totalRows, answered: selectedRows.size })
          };
        }
      }

      if (question.type === "image") {
        const mode = question.imageAnswerMode || "select";
        if (mode === "draw") {
          if (!answer || typeof answer !== "string" || answer.trim() === "") {
            return {
              isValid: false,
              message: t('questionRequired', { number: i + 1 })
            };
          }
        } else if (!answer || !Array.isArray(answer) || answer.length === 0) {
          return {
            isValid: false,
            message: t('questionRequired', { number: i + 1 })
          };
        }
      }

      if (question.type === "rating_scale") {
        const v = answer;
        if (v === undefined || v === null || v === "") {
          return {
            isValid: false,
            message: t('questionRequired', { number: i + 1 }),
          };
        }
        const n = Number(v);
        if (Number.isNaN(n) || n < 0 || n > 10) {
          return {
            isValid: false,
            message: t('questionRequired', { number: i + 1 }),
          };
        }
      }
    }

    return { isValid: true, message: "" };
  };

  // --- Formu kaydet ---
  const handleSave = async () => {
    // Validasyon kontrolü
    const validation = validateForm();
    if (!validation.isValid) {
      setValidationError({ open: true, message: validation.message });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!formInfo) return;

      const formattedQuestions = formInfo?.questions?.map((q, index) => {
        let selectedAnswer = answers[index] || "";
        let optionLevel = null;

        // Multiple choice soruları için option_level değerini al
        if (q.type === "multiple_choice") {
          optionLevel = answers[`${index}_option_level`] || null;
          // Eğer option_level yoksa, seçilen cevabı options array'inde bulup option_level'ı al
          if (optionLevel === null && selectedAnswer) {
            const selectedOption = q.options?.find(opt => opt.option === selectedAnswer);
            if (selectedOption && selectedOption.option_level !== undefined) {
              optionLevel = selectedOption.option_level;
            }
          }
        }

        // Tablo soruları için cevapları formatla
        // Her hücre bağımsız olduğu için, seçili hücreleri hücre bazlı key-value formatında gönder
        if (q.type === "table") {
          const tableAnswers = answers[index] || {};
          // Her hücre için "rowIndex_colIndex": "headerValue" formatında gönder
          const formattedTableAnswers = {};
          // Her satır için seçilen hücrenin importance değerini tutacak array
          const optionLevels = [];
          
          Object.keys(tableAnswers).forEach(cellKey => {
            if (tableAnswers[cellKey] === true) {
              const [rowIdx, headerIdx] = cellKey.split('_');
              const rowIndex = parseInt(rowIdx);
              const headerIndex = parseInt(headerIdx);
              
              // Her hücre için benzersiz key: "rowIndex_colIndex"
              formattedTableAnswers[cellKey] = q.tableHeaders[headerIndex];
              
              // Her satır için seçilen hücrenin importance değerini al
              if (q.tableHeaderImportance && q.tableHeaderImportance[headerIndex] !== undefined) {
                optionLevels[rowIndex] = q.tableHeaderImportance[headerIndex];
              }
            }
          });
          
          selectedAnswer = JSON.stringify(formattedTableAnswers);
          
          // Eğer importance değerleri varsa, satır sırasına göre sıralanmış array olarak option_level gönder
          if (optionLevels.length > 0) {
            // Boş index'leri kaldırmak için filter kullan, satır sırasına göre sıralı array oluştur
            const sortedOptionLevels = [];
            for (let i = 0; i < q.tableRows; i++) {
              if (optionLevels[i] !== undefined) {
                sortedOptionLevels.push(optionLevels[i]);
              }
            }
            optionLevel = sortedOptionLevels.length > 0 ? sortedOptionLevels : null;
          }
        }

        if (q.type === "image") {
          const mode = q.imageAnswerMode || "select";
          if (mode === "draw") {
            selectedAnswer = answers[index] || "";
            optionLevel = null;
          } else {
            const selectedAreas = answers[index] || [];
            selectedAnswer = JSON.stringify(selectedAreas);
            optionLevel = selectedAreas.reduce((total, area) => {
              const areaValue = parseFloat(area.value) || 0;
              return total + areaValue;
            }, 0);
          }
        }

        if (q.type === "rating_scale") {
          const num = Number(answers[index]);
          const v = Number.isNaN(num)
            ? 0
            : Math.max(0, Math.min(10, Math.round(num)));
          selectedAnswer = v;
          const imp = Array.isArray(q.scaleImportances) ? q.scaleImportances : [];
          const raw = Number(imp[v]);
          optionLevel = Number.isNaN(raw) ? 0 : raw;
        }

        return {
          question: q.question,
          answer: selectedAnswer,
          type: q.type,
          ...(optionLevel !== null && { option_level: optionLevel }),
          ...(q.hasImportance !== undefined && { hasImportance: q.hasImportance }),
          ...(q.type === "table" && {
            tableHeaders: q.tableHeaders,
            tableQuestions: q.tableQuestions,
            tableRows: q.tableRows,
            tableCols: q.tableCols,
            tableHeaderImportance: q.tableHeaderImportance,
          }),
          ...(q.type === "image" && {
            imageUrl: q.imageUrl,
            imageAnswerMode: q.imageAnswerMode || "select",
            areas: q.areas,
          }),
          ...(q.type === "rating_scale" && {
            value: typeof selectedAnswer === "number" ? selectedAnswer : Number(selectedAnswer),
            scaleImportances: q.scaleImportances,
            minLabel: q.minLabel,
            maxLabel: q.maxLabel,
            positiveEnd: q.positiveEnd || "min",
          }),
        };
      });

      const data = {
        token: token, // ✅ public form için gerekli
        name: formInfo.name,
        description: formInfo.description,
        type: formInfo.type,
        questions: formattedQuestions,
      };

      console.log("Gönderilecek data:", data);

      const response = await formService.formAnswerPublic(data);
      console.log("Form başarıyla gönderildi:", response);

      setShowAlert(true);
       // ✅ Teşekkür ekranına geç
      setTimeout(() => {
        setIsSubmitted(true);
      }, 1000);
    } catch (error) {
      console.error("Form gönderilemedi:", error);
      setSubmitError({ 
        open: true, 
        message: error.response?.data?.message || error.message || t('formSubmitError') 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        height: "auto",
        background: "white",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Seo
        title={tSeo("publicForm.title")}
        description={tSeo("publicForm.description")}
        keywords={tSeo("publicForm.keywords")}
        noindex
      />
      <PublicFormPageTopbar />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          p: { xs: 2, md: 4 },
          pb: 6,
          background: "white",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "900px", mt: { xs: 2, md: 5 } }}>
          {isSubmitted ? (
            // ✅ TEŞEKKÜR EKRANI
            <Card
              sx={{
                borderRadius: 4,
                background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
                boxShadow: `0 24px 48px rgba(0, 0, 0, 0.35)`,
                border: `1px solid ${colors.greenAccent[500]}40`,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                    boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 60, color: "white" }} />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: "white",
                    letterSpacing: 0.5,
                  }}
                >
                  {t('formSubmittedSuccessfully')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  {t('thankYouMessage')}
                </Typography>
              </CardContent>
            </Card>
          ) : alreadySubmittedInfo ? (
            <Card
              sx={{
                borderRadius: 4,
                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
                boxShadow: `0 24px 48px rgba(0, 0, 0, 0.35)`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                    boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`,
                  }}
                >
                  <DoneAllIcon sx={{ fontSize: 60, color: "white" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: "white",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("formAlreadySubmittedTitle")}
                </Typography>
                {alreadySubmittedInfo.formName && (
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "rgba(255, 255, 255, 0.95)",
                      fontWeight: 600,
                    }}
                  >
                    {t("formAlreadySubmittedFormLabel", {
                      name: alreadySubmittedInfo.formName,
                    })}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.92)",
                    fontWeight: 400,
                    lineHeight: 1.7,
                    mb: 2,
                  }}
                >
                  {t("formAlreadySubmittedMessage")}
                </Typography>
                {alreadySubmittedInfo.answeredAt && (() => {
                  const d = new Date(alreadySubmittedInfo.answeredAt);
                  const formatted =
                    !Number.isNaN(d.getTime())
                      ? d.toLocaleString(getDateLocale(), {
                          dateStyle: "long",
                          timeStyle: "short",
                        })
                      : null;
                  return formatted ? (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.95)",
                        fontWeight: 600,
                        mt: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.15)",
                        display: "inline-block",
                      }}
                    >
                      {t("formAlreadySubmittedAt", { date: formatted })}
                    </Typography>
                  ) : null;
                })()}
              </CardContent>
            </Card>
          ) : loading ? (
            // ✅ LOADING EKRANI
            <Card
              sx={{
                borderRadius: 4,
                background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[500]} 100%)`,
                boxShadow: `0 12px 24px rgba(0, 0, 0, 0.25)`,
                border: `1px solid ${colors.primary[300]}30`,
              }}
            >
              <CardContent
                sx={{
                  p: 6,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <CircularProgress
                  sx={{
                    color: colors.greenAccent[500],
                  }}
                  size={60}
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 600,
                  }}
                >
                  {t('loadingForm')}
                </Typography>
              </CardContent>
            </Card>
          ) : error ? (
            // ✅ ERROR EKRANI
            <Card
              sx={{
                borderRadius: 4,
                background: `linear-gradient(135deg, ${colors.redAccent[700]} 0%, ${colors.redAccent[600]} 100%)`,
                boxShadow: `0 12px 24px rgba(0, 0, 0, 0.25)`,
                border: `1px solid ${colors.redAccent[500]}40`,
              }}
            >
              <CardContent
                sx={{
                  p: 6,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  {t('errorLoadingForm')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  {error}
                </Typography>
              </CardContent>
            </Card>
          ) : formInfo ? (
            // ✅ FORM GÖRÜNÜMÜ
            <>
              {/* Form Header Card */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
                  boxShadow: `0 16px 32px rgba(0, 0, 0, 0.3)`,
                  border: `1px solid ${colors.blueAccent[500]}40`,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "white",
                      mb: 2,
                      letterSpacing: 0.5,
                    }}
                  >
                    {formInfo.name || t('form')}
                  </Typography>
                  {formInfo.description && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        lineHeight: 1.8,
                        fontSize: "1.1rem",
                      }}
                    >
                      {formInfo.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Form Statistics Card */}
              {formStatus && (
                <Card
                  sx={{
                    mb: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${colors.primary[700]} 0%, ${colors.primary[600]} 100%)`,
                    boxShadow: `0 12px 24px rgba(0, 0, 0, 0.25)`,
                    border: `1px solid ${colors.primary[400]}30`,
                    overflow: "hidden",
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        pb: 2,
                        borderBottom: `2px solid ${colors.blueAccent[700]}40`,
                      }}
                    >
                      <AssessmentIcon
                        sx={{
                          fontSize: 32,
                          color: colors.greenAccent[500],
                          mr: 2,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: colors.grey[100],
                        }}
                      >
                        {t('formStatistics')}
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      {/* Current Form Number */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[600]} 100%)`,
                            borderRadius: 3,
                            textAlign: "center",
                            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                            border: `1px solid ${colors.blueAccent[500]}40`,
                          }}
                        >
                          <AssignmentIcon
                            sx={{
                              fontSize: 36,
                              color: "white",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              mb: 0.5,
                            }}
                          >
                            {invitation?.count || formStatus?.currentInvitationCount || "-"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.9)",
                              fontWeight: 500,
                            }}
                          >
                            {t('thisIsForm', { count: invitation?.count || formStatus?.currentInvitationCount || "-" })}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Total Forms Sent */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
                            borderRadius: 3,
                            textAlign: "center",
                            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                            border: `1px solid ${colors.greenAccent[500]}40`,
                          }}
                        >
                          <ScheduleIcon
                            sx={{
                              fontSize: 36,
                              color: "white",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              mb: 0.5,
                            }}
                          >
                            {formStatus?.currentFormTotalCount || 0}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.9)",
                              fontWeight: 500,
                            }}
                          >
                            {t('totalFormsSent')}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Completed Forms */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                            borderRadius: 3,
                            textAlign: "center",
                            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                            border: `1px solid ${colors.greenAccent[400]}40`,
                          }}
                        >
                          <DoneAllIcon
                            sx={{
                              fontSize: 36,
                              color: "white",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              mb: 0.5,
                            }}
                          >
                            {formStatus?.completedCount || 0}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.9)",
                              fontWeight: 500,
                            }}
                          >
                            {t('completedForms')}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Pending Forms (excluding current) */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                            borderRadius: 3,
                            textAlign: "center",
                            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                            border: `1px solid ${colors.primary[300]}40`,
                          }}
                        >
                          <ScheduleIcon
                            sx={{
                              fontSize: 36,
                              color: "white",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              mb: 0.5,
                            }}
                          >
                            {Math.max(0, (formStatus?.pendingCount || 1) - 1)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.9)",
                              fontWeight: 500,
                            }}
                          >
                            {t('otherPendingForms')}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Previous Completed Forms List */}
                    {formStatus?.completedInvitations &&
                      formStatus.completedInvitations.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: colors.grey[100],
                              mb: 2,
                            }}
                          >
                            {t('previouslyCompletedForms')}
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {formStatus.completedInvitations
                              .sort((a, b) => (b.count || 0) - (a.count || 0))
                              .map((completedInv, idx) => (
                                <Paper
                                  key={completedInv.id || idx}
                                  sx={{
                                    p: 2,
                                    background: `linear-gradient(135deg, ${colors.greenAccent[800]} 0%, ${colors.greenAccent[700]} 100%)`,
                                    borderRadius: 2,
                                    border: `1px solid ${colors.greenAccent[600]}40`,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    boxShadow: `0 2px 8px rgba(0, 0, 0, 0.15)`,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 600,
                                        color: "white",
                                        mb: 0.5,
                                      }}
                                    >
                                      {t('formNumber', { count: completedInv.count || "-" })} - {t('completed')}
                                    </Typography>
                                    {completedInv.answered_at && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "rgba(255, 255, 255, 0.8)",
                                        }}
                                      >
                                        {t('completedOn')}{" "}
                                        {new Date(completedInv.answered_at).toLocaleDateString(getDateLocale(), {
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </Typography>
                                    )}
                                  </Box>
                                  <CheckCircleIcon
                                    sx={{
                                      color: "white",
                                      fontSize: 32,
                                    }}
                                  />
                                </Paper>
                              ))}
                          </Box>
                        </Box>
                      )}
                  </CardContent>
                </Card>
              )}

              {formInfo.questions?.map((field, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[500]} 100%)`,
                    boxShadow: `0 12px 24px rgba(0, 0, 0, 0.25)`,
                    border: `1px solid ${colors.primary[300]}30`,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 16px 32px rgba(0, 0, 0, 0.3)`,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        pb: 2,
                        borderBottom: `2px solid ${colors.blueAccent[700]}40`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                          fontWeight: 700,
                          color: "white",
                          fontSize: "1.1rem",
                          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: colors.grey[100],
                          flex: 1,
                        }}
                      >
                        {field.question}
                      </Typography>
                    </Box>

                    {field.type === "text" && (
                      <TextField
                        onChange={(e) =>
                          handleAnswerChange(index, e.target.value)
                        }
                        value={answers[index] || ""}
                        placeholder={t('typeYourAnswerHere')}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: colors.primary[700],
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: `${colors.blueAccent[600]}60`,
                              borderWidth: 2,
                            },
                            "&:hover fieldset": {
                              borderColor: colors.blueAccent[500],
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: colors.greenAccent[500],
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: colors.grey[100],
                            fontSize: "1rem",
                          },
                        }}
                      />
                    )}

                    {field.type === "multiple_choice" && (
                      <FormControl fullWidth>
                        <RadioGroup
                          onChange={(e) => {
                            const selectedOption = field.options?.find(opt => opt.option === e.target.value);
                            handleAnswerChange(index, e.target.value, selectedOption?.option_level || null);
                          }}
                          value={answers[index] || ""}
                        >
                          {field.options?.map((option, optionIdx) => (
                            <Paper
                              key={optionIdx}
                              sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: colors.primary[700],
                                border: `2px solid ${
                                  answers[index] === option.option
                                    ? colors.greenAccent[500]
                                    : `${colors.primary[400]}40`
                                }`,
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: colors.primary[650],
                                  borderColor: colors.blueAccent[500],
                                  transform: "translateX(4px)",
                                },
                              }}
                              onClick={() =>
                                handleAnswerChange(index, option.option, option.option_level)
                              }
                            >
                              <FormControlLabel
                                value={option.option}
                                control={
                                  <Radio
                                    sx={{
                                      color: colors.blueAccent[400],
                                      "&.Mui-checked": {
                                        color: colors.greenAccent[500],
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      color: colors.grey[100],
                                      fontWeight: 500,
                                      fontSize: "1rem",
                                    }}
                                  >
                                    {option.option}
                                  </Typography>
                                }
                                sx={{ m: 0 }}
                              />
                            </Paper>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}

                    {field.type === "rating_scale" && (
                      <Box sx={{ mt: 1, maxWidth: 720 }}>
                        <RatingScaleSlider
                          value={
                            typeof answers[index] === "number" && !Number.isNaN(answers[index])
                              ? answers[index]
                              : 5
                          }
                          onChange={(n) => handleAnswerChange(index, n)}
                          minLabel={field.minLabel || "0"}
                          maxLabel={field.maxLabel || "10"}
                          positiveEnd={field.positiveEnd === "max" ? "max" : "min"}
                          colors={colors}
                        />
                      </Box>
                    )}

                    {field.type === "image" && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                          <ImageIcon sx={{ color: colors.greenAccent[500] }} />
                          <Typography sx={{ color: colors.grey[100], fontWeight: 700 }}>
                            {field.imageAnswerMode === "draw" ? "Draw on the image" : "Select areas on the image"}
                          </Typography>
                        </Box>

                        {(field.imageAnswerMode || "select") === "draw" ? (
                          <ImageDrawingAnswer
                            imageUrl={field.imageUrl}
                            value={typeof answers[index] === "string" ? answers[index] : ""}
                            onChange={(dataUrl) => handleAnswerChange(index, dataUrl)}
                          />
                        ) : (
                          <Box>
                            <CartilageMap
                              imageUrl={field.imageUrl}
                              areas={field.areas || []}
                              selectedAreas={answers[index] || []}
                              multiSelect={true}
                              onAreaSelect={(area) => {
                                const currentSelected = answers[index] || [];
                                const areaIndex = currentSelected.findIndex((a) => a.id === area.id);
                                if (areaIndex > -1) {
                                  handleAnswerChange(index, currentSelected.filter((a) => a.id !== area.id));
                                } else {
                                  handleAnswerChange(index, [...currentSelected, area]);
                                }
                              }}
                              disabled={false}
                              showLabels={true}
                            />

                            {Array.isArray(answers[index]) && answers[index].length > 0 && (
                              <Typography sx={{ mt: 1.5, color: colors.greenAccent[300] }}>
                                Selected areas: {answers[index].length}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {field.type === "table" && (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 3,
                            flexWrap: "wrap",
                          }}
                        >
                          <TableChartIcon
                            sx={{
                              fontSize: 32,
                              color: colors.greenAccent[500],
                            }}
                          />
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            {t('tableQuestion')}
                          </Typography>
                          <Box
                            sx={{
                              ml: "auto",
                              display: "flex",
                              gap: 1.5,
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[600]} 100%)`,
                                border: `1px solid ${colors.blueAccent[500]}`,
                                boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: colors.grey[100],
                                }}
                              >
                                📝 {t('rows', { count: field.tableRows })}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
                                border: `1px solid ${colors.greenAccent[500]}`,
                                boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: colors.grey[100],
                                }}
                              >
                                📊 {t('columns', { count: field.tableCols })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Paper
                          sx={{
                            overflowX: "auto",
                            borderRadius: 3,
                            border: `2px solid ${colors.greenAccent[700]}60`,
                            boxShadow: `0 8px 24px rgba(0, 0, 0, 0.3)`,
                            background: `linear-gradient(135deg, ${colors.primary[700]} 0%, ${colors.primary[600]} 100%)`,
                          }}
                        >
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ 
                                  background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                                  fontWeight: "bold",
                                  border: `2px solid ${colors.blueAccent[600]}`,
                                  color: colors.grey[100],
                                  fontSize: "1rem",
                                  textAlign: "center",
                                  minWidth: 180
                                }}>
                                  ❓ {t('questions')}
                                </TableCell>
                                {field.tableHeaders?.map((header, headerIdx) => (
                                  <TableCell 
                                    key={headerIdx}
                                    sx={{ 
                                      background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                                      fontWeight: "bold",
                                      border: `2px solid ${colors.greenAccent[600]}`,
                                      minWidth: 140,
                                      padding: 2,
                                      textAlign: "center"
                                    }}
                                  >
                                    <Typography 
                                      sx={{ 
                                        fontSize: "0.95rem",
                                        fontWeight: "bold",
                                        color: colors.grey[100]
                                      }}
                                    >
                                      {header}
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {field.tableQuestions?.map((question, questionIdx) => (
                                <TableRow 
                                  key={questionIdx}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: colors.primary[600]
                                    },
                                    transition: "background-color 0.2s"
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      background: `linear-gradient(135deg, ${colors.blueAccent[800]} 0%, ${colors.blueAccent[900]} 100%)`,
                                      border: `2px solid ${colors.blueAccent[700]}60`,
                                      minWidth: 180,
                                      color: colors.grey[100],
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                      py: 2.5,
                                    }}
                                  >
                                    {question}
                                  </TableCell>
                                  {field.tableHeaders?.map((header, headerIdx) => {
                                    const cellKey = `${questionIdx}_${headerIdx}`;
                                    const isSelected = answers[index]?.[cellKey] === true;
                                    
                                    // Aynı satırda başka bir hücre seçili mi kontrol et
                                    const hasOtherSelectionInRow = Object.keys(answers[index] || {})
                                      .some(key => {
                                        if (key === cellKey) return false;
                                        const [rowIdx] = key.split('_');
                                        return rowIdx === String(questionIdx) && answers[index]?.[key] === true;
                                      });
                                    
                                    // Bu hücre disabled olmalı mı? (Aynı satırda başka bir hücre seçiliyse ve bu hücre seçili değilse)
                                    const isDisabled = hasOtherSelectionInRow && !isSelected;
                                    
                                    const handleCellClick = () => {
                                      if (!isDisabled) {
                                        handleTableAnswerChange(
                                          index,
                                          questionIdx,
                                          headerIdx,
                                          header
                                        );
                                      }
                                    };
                                    
                                    return (
                                      <TableCell
                                        key={headerIdx}
                                        onClick={handleCellClick}
                                        sx={{
                                          border: `2px solid ${isSelected ? colors.greenAccent[500] : colors.greenAccent[700]}40`,
                                          textAlign: "center",
                                          background: isSelected
                                            ? `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`
                                            : isDisabled
                                            ? `linear-gradient(135deg, ${colors.primary[800]} 0%, ${colors.primary[700]} 100%)`
                                            : `linear-gradient(135deg, ${colors.primary[650]} 0%, ${colors.primary[600]} 100%)`,
                                          py: 2.5,
                                          transition: "all 0.2s ease",
                                          cursor: isDisabled ? "not-allowed" : "pointer",
                                          opacity: isDisabled ? 0.5 : 1,
                                          position: "relative",
                                          userSelect: "none", // Text seçimini engelle
                                          "&:hover": {
                                            backgroundColor: isDisabled
                                              ? undefined
                                              : isSelected
                                              ? colors.greenAccent[600]
                                              : colors.primary[550],
                                            transform: isDisabled ? undefined : "scale(1.02)",
                                          },
                                        }}
                                      >
                                        <Checkbox
                                          checked={isSelected}
                                          disabled={isDisabled}
                                          onChange={handleCellClick}
                                          onClick={(e) => {
                                            // Checkbox tıklamasını da handleCellClick'e yönlendir
                                            e.stopPropagation();
                                            handleCellClick();
                                          }}
                                          size="large"
                                          sx={{
                                            color: colors.greenAccent[600],
                                            pointerEvents: "none", // Checkbox'ın tıklamayı engellemesini önle, sadece görsel
                                            "& .MuiSvgIcon-root": {
                                              fontSize: "2rem",
                                            },
                                            "&.Mui-checked": {
                                              color: colors.greenAccent[400],
                                            },
                                            "&.Mui-disabled": {
                                              color: colors.grey[600],
                                            },
                                            "& .MuiTouchRipple-root": {
                                              display: "none", // Ripple effect'i tamamen kaldır
                                            },
                                          }}
                                        />
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                        <Box
                          sx={{
                            mt: 3,
                            p: 2.5,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${colors.blueAccent[800]}40 0%, ${colors.blueAccent[700]}40 100%)`,
                            border: `1px solid ${colors.blueAccent[600]}60`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: colors.blueAccent[300],
                              fontWeight: 500,
                              fontStyle: "italic",
                            }}
                          >
                            {t('selectOneOptionPerRow')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 4,
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                    color: "white",
                    fontWeight: 700,
                    px: 6,
                    py: 1.5,
                    fontSize: "1.1rem",
                    borderRadius: 3,
                    boxShadow: `0 8px 24px ${colors.greenAccent[700]}50`,
                    border: `1px solid ${colors.greenAccent[500]}40`,
                    textTransform: "none",
                    letterSpacing: 0.5,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 32px ${colors.greenAccent[600]}60`,
                    },
                  }}
                  disabled={isSubmitting}
                  onClick={handleSave}
                >
                  {isSubmitting ? t('submitting') : t('submitForm')}
                </Button>
              </Box>

              <Snackbar
                open={showAlert}
                autoHideDuration={2000}
                onClose={() => setShowAlert(false)}
              >
                <Alert
                  severity="success"
                  variant="filled"
                  onClose={() => setShowAlert(false)}
                >
                  {t('formSubmittedSuccess')}
                </Alert>
              </Snackbar>

              {/* Validation Error Snackbar */}
              <SnackbarComponent
                open={validationError.open}
                autoHideDuration={5000}
                onClose={() => setValidationError({ open: false, message: "" })}
                message={validationError.message}
                severity="error"
              />

              {/* Submit Error Snackbar */}
              <SnackbarComponent
                open={submitError.open}
                autoHideDuration={5000}
                onClose={() => setSubmitError({ open: false, message: "" })}
                message={submitError.message}
                severity="error"
              />
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
